/**
 * Analytics del asistente (cliente): CustomEvent local + POST opcional a /api/assistant/events.
 */

const EVENT_NAME = 'soluqr:assistant-analytics';

const DEDUPE_TTL_MS = 2500;
const DEDUPE_MAX_KEYS = 96;
/** @type {Map<string, number>} */
const _dedupeUntil = new Map();

function _pruneDedupe(now) {
  for (const [k, exp] of _dedupeUntil) {
    if (exp < now) _dedupeUntil.delete(k);
  }
  if (_dedupeUntil.size <= DEDUPE_MAX_KEYS) return;
  const keys = [..._dedupeUntil.keys()];
  const drop = _dedupeUntil.size - DEDUPE_MAX_KEYS;
  for (let i = 0; i < drop; i++) _dedupeUntil.delete(keys[i]);
}

function _fingerprintDetail(detail) {
  return JSON.stringify({
    e: detail.event,
    b: detail.business_id ?? null,
    t: detail.timestamp ?? null,
    i: detail.detected_intent ?? null,
    p: (detail.product_ids_shown || []).join(','),
    s: (detail.suggested_product_ids_shown || []).join(','),
    u: String(detail.user_text || '').slice(0, 48),
    pid: detail.product_id ?? null,
    src: detail.source ?? null,
    cc: detail.cart_count ?? null,
    oc: detail.order_choice ?? null,
    hp: detail.had_products ?? null,
    hsp: detail.had_suggested_products ?? null,
  });
}

function _shouldSkipDuplicate(detail) {
  const now = Date.now();
  for (const [k, exp] of _dedupeUntil) {
    if (exp < now) _dedupeUntil.delete(k);
  }
  const fp = _fingerprintDetail(detail);
  if (_dedupeUntil.has(fp)) return true;
  _dedupeUntil.set(fp, now + DEDUPE_TTL_MS);
  _pruneDedupe(now);
  return false;
}

function persistAssistantEventToBackend(detail) {
  try {
    const rawBase =
      typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_URL
        ? String(process.env.REACT_APP_BACKEND_URL)
        : '';
    const base = rawBase.replace(/\/$/, '');
    if (!base || typeof fetch === 'undefined') return;
    if (_shouldSkipDuplicate(detail)) return;

    const payload = { ...detail };
    if (!Array.isArray(payload.product_ids_shown)) payload.product_ids_shown = [];
    if (!Array.isArray(payload.suggested_product_ids_shown)) {
      payload.suggested_product_ids_shown = [];
    }
    if (payload.timestamp == null || typeof payload.timestamp !== 'number') {
      payload.timestamp = Date.now();
    }

    const url = `${base}/api/assistant/events`;
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'omit',
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* no bloquear UX */
  }
}

function ensureAssistantAnalyticsPersistListener() {
  if (typeof window === 'undefined' || window.__soluqrAssistantAnalyticsPersistAttached) return;
  window.__soluqrAssistantAnalyticsPersistAttached = true;
  window.addEventListener(EVENT_NAME, (ev) => {
    try {
      const d = ev && ev.detail;
      if (!d || !d.event) return;
      persistAssistantEventToBackend(d);
    } catch {
      /* ignore */
    }
  });
}

ensureAssistantAnalyticsPersistListener();

/**
 * @param {object} p
 * @param {string} [p.businessId]
 * @param {string} [p.currentStep]
 * @param {object} [p.sessionMemory]
 * @param {string|null} [p.servicePreference]
 * @param {string|null} [p.userText]
 */
export function buildAssistantAnalyticsBase({
  businessId,
  currentStep,
  sessionMemory = {},
  servicePreference = null,
  userText = null,
}) {
  const mem = sessionMemory || {};
  return {
    business_id: businessId ?? null,
    current_step: currentStep ?? null,
    service_preference: servicePreference ?? mem.servicePreference ?? null,
    party_size: mem.partySize ?? null,
    mood: mem.mood ?? null,
    last_intent: mem.lastIntent ?? null,
    user_text: userText != null ? String(userText).slice(0, 160) : null,
    timestamp: Date.now(),
  };
}

/**
 * Heurística ligera sobre la respuesta del motor (sin tocar todas las ramas internas).
 * @param {object|null} ans
 * @returns {string}
 */
export function inferAssistantIntent(ans) {
  if (!ans || typeof ans !== 'object') return 'unknown';
  if (ans.orderChoice) return `order_choice_${ans.orderChoice}`;
  if (ans.products?.length && ans.suggestedProducts?.length) return 'products_with_contextual';
  if (ans.products?.length) return 'products_shown';
  if (!ans.products?.length && ans.text) {
    const t = String(ans.text);
    if (
      /\bTe ayudo altiro\b/.test(t) ||
      /\bEntiendo, déjame ayudarte\b/.test(t) ||
      /\bPuedo ayudarte a pedir\b/.test(t)
    ) {
      return 'fallback_generic';
    }
  }
  if (ans.followUp) return 'conversational_composite';
  if (ans.text && !ans.products?.length) return 'text_only';
  return 'unknown';
}

/**
 * @param {string} eventName
 * @param {Record<string, unknown>} payload
 */
export function trackAssistantEvent(eventName, payload) {
  const detail = {
    event: eventName,
    ...(payload && typeof payload === 'object' ? payload : {}),
  };
  if (typeof window !== 'undefined') {
    try {
      ensureAssistantAnalyticsPersistListener();
      window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail }));
    } catch {
      /* ignore */
    }
  }
  if (process.env.NODE_ENV === 'development' && typeof console !== 'undefined' && console.debug) {
    console.debug('[assistant-analytics]', eventName, detail);
  }
}

export const ASSISTANT_ANALYTICS_EVENT = EVENT_NAME;
